"use client";

import { useAuth } from "@/providers/AuthProvider";
import { getCurrentAttribution } from "@codiea/tracking";
import { useCodiea } from "@codiea/tracking/react";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";

/**
 * App-owned page_view tracker: same as CodieaPageViews, plus userId from auth.
 * Mount inside AuthProvider (and Suspense for useSearchParams).
 */
export function CodieaAuthenticatedPageViews() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { user, isAuthLoading } = useAuth();
  const { track, captureAttribution } = useCodiea();
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    // Wait until session hydrate finishes so logged-in users get userId on first view.
    if (isAuthLoading) return;

    const search = searchParams?.toString() ?? "";
    const key = `${pathname}?${search}`;
    if (lastKey.current === key) return;
    lastKey.current = key;

    try {
      captureAttribution();
    } catch {
      // Attribution capture should not block the page view.
    }

    const attribution = getCurrentAttribution();

    void track({
      event: "page_view",
      userId: user?.id != null ? String(user.id) : undefined,
      properties: {
        page_path: pathname || "/",
        ...attribution,
      },
    });
  }, [
    pathname,
    searchParams,
    isAuthLoading,
    user?.id,
    track,
    captureAttribution,
  ]);

  return null;
}
